import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { BsTable } from "react-icons/bs";
import { PDFExport } from "@progress/kendo-react-pdf";

import {
  downloadSeparatePdfs,
  generateCombinedDividendStatementPdf,
  getUnitHolderDividendStatement,
  // NEW — mirrors getUnitHolderDividendStatement but hits the
  // getUnitholderStatementByFund backend endpoint (amc_code + fund_code +
  // date range, NO folio_no). See report.service.additions.ts for a
  // suggested implementation to drop into report.service.ts.
  getUnitHolderStatementByFund,
} from "../../stores/services/report.service";
import { getFundByAmc } from "../../stores/services/funds.service";

import ReportHeader from "./report-header";
import DividendCard from "../dividend/AnnouncementListing";
import ReportTemplates from "../../components/ReportTemplate/ReportTemplate";
import { UnitHolderSearchForm } from "./unitholder-statement/components";

import { useUnitHolderSearch } from "../../hooks/useSearchUnitholder";
import { getKarachiDate, numberWithCommas } from "../../utils/customFunction";
import { useSelector } from "react-redux";
import { useCustomStylesDropDown } from "../../assets/css/customStyle";

import html2pdf from "html2pdf.js";
import { exportToTxt, generateExcel } from "../../utils/customFunction";

// Column set for this report — picked through the same ReportTemplates
// toolbar used by the Unitholder Statement page, just with the dividend
// statement's own fields instead of TXN ID / Load / Tax / Net Amount.
const DIVIDEND_COLUMN_HEADINGS = [
  { label: "Date", value: "Date" },
  { label: "Transaction", value: "Transaction" },
  { label: "Applicable Price", value: "Applicable Price" },
  { label: "Units", value: "Units" },
  { label: "Amount", value: "Amount" },
  { label: "Balance Units", value: "Balance Units" },
];

const DIVIDEND_FIXED_ORDER = [
  "Date",
  "Transaction",
  "Applicable Price",
  "Units",
  "Amount",
  "Balance Units",
];

// Same semantics as the Unitholder Statement page: which txnr_types add vs
// subtract units when walking forPeriodTxns to build a running balance.
const unitSoldArr = [
  "cgtrefund",
  "refundofcapital",
  "dividendreinvestment",
  "dividend",
  "bonus",
  "transferlienin",
  "transferin",
  "conversionin",
  "saleofunit",
];
const redeemedArr = [
  "zakatondividend",
  "zakatonbonusencashment",
  "dividendpaid",
  "taxondividend",
  "taxonbonusencashment",
  "bonusencashment",
  "transferlienout",
  "transferout",
  "conversionout",
  "redemptionofunits",
];

// Dividend / refund-of-capital breakdown rows are identified by a `label`
// field (set on the backend). Only these two labels carry units that should
// move the running balance — the rest (Gross Dividend, Taxable Dividend,
// Tax on Dividend, Dividend Payment) are amount-only.
const UNIT_ADDING_DIVIDEND_LABELS = ["Refund of Capital", "Dividend Reinvest"];
const SKIP_DIVIDEND_ENTRIES = ["DRI", "ROC", "TXD", "TOD", "DPY"];

// How many unitholder reports are shown / requested per "page" in
// fund-only (all-unitholders) mode.
const UNITHOLDERS_PER_PAGE = 10;

const UnitHolderDividendStatement = () => {
  const contentRef = useRef(null);
  // Off-screen container used only while generating one-PDF-per-unitholder
  // downloads, so we don't have to disturb what's on screen for the user.
  const exportRef = useRef(null);
  const pdfExportComponent = useRef<PDFExport>(null);

  const email = sessionStorage.getItem("email") || "";
  const amc_code = sessionStorage.getItem("amc_code") || "";
  const theme = useSelector((state: any) => state.ThemeModeReducer.Theme);
  const txnTypes = useSelector((state: any) => state.txnTypes);

  const [fund, setFund] = useState("all");
  const [folioNo, setFolioNo] = useState("");
  const [fromDate, setFromDate] = useState(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));

  const [unitHolderStatement, setUnitHolderStatement] = useState<any[]>([]);
  const [unitHolderData, setUnitHolderData] = useState<any>([]);
  const [allFunds, setAllFunds] = useState<any[]>([]);

  const [resFolioNo, setResFolioNo] = useState("");
  const [resFromDate, setResFromDate] = useState(moment().format("YYYY-MM-DD"));
  const [resToDate, setResToDate] = useState(moment().format("YYYY-MM-DD"));

  const [loading, setLoading] = useState(false);
  const [folioLoading, setFolioLoading] = useState(false);

  const [folioNoErr, setFolioNoErr] = useState("");
  const [fromDateErr, setFromDateErr] = useState("");
  const [toDateErr, setToDateErr] = useState("");

  // ---- NEW: fund-only ("all unitholders holding this fund") mode ----
  // When the user searches with a specific fund selected and NO folio
  // number, we hit getUnitholderStatementByFund instead, which returns one
  // statement-shaped record per unitholder holding that fund.
  const [isMultiMode, setIsMultiMode] = useState(false);
  const [multiUnitholders, setMultiUnitholders] = useState<any[]>([]);
  const [unitholdersToShow, setUnitholdersToShow] =
    useState(UNITHOLDERS_PER_PAGE);
  const [downloadingCombined, setDownloadingCombined] = useState(false);
  const [downloadingSeparate, setDownloadingSeparate] = useState(false);
  const [separateProgress, setSeparateProgress] = useState({
    current: 0,
    total: 0,
  });
  // Holder currently being rendered into the hidden export container while
  // looping through "download separate PDFs".
  const [exportSingleHolder, setExportSingleHolder] = useState<any>(null);

  // Report customization — same shape/role as the Unitholder Statement page.
  const [selectedValues, setSelectedValues] = useState<any>({
    heading: "",
    sub_heading: "",
    columns: DIVIDEND_COLUMN_HEADINGS?.map?.((col) => col?.value),
    balance: ["Opening Balance", "Closing Balance"],
  });
  const [fontSizeVal, setFontSizeVal] = useState<any>(18);
  const [paperSizeVal, setPaperSizeVal] = useState("A4");
  const [layoutVal, setLayoutVal] = useState("Portrait");
  const [marginPDF, setMarginPDF] = useState("1cm");
  const [scalePDF, setScalePDF] = useState(0.5);
  const [itemsPerPage, setItemsPerPage] = useState<any>(10);
  const [itemsToShow, setItemsToShow] = useState(10);
  const { customStyles } = useCustomStylesDropDown();
  const [screenSizePerPdf, setScreenSizePerPdf] = useState();
  const [dimensionOfPages, setDimensionOfPages] = useState<any>();

  const {
    unitholderOptions,
    unitholderLoading,
    fetchInitialUnitholders,
    searchUnitholders,
  } = useUnitHolderSearch(email);

  const [unitholderSearchInput, setUnitholderSearchInput] = useState("");

  useEffect(() => {
    const getAllFunds = async () => {
      setFolioLoading(true);
      try {
        const response = await getFundByAmc(email, amc_code, "");
        setAllFunds(response.data.data);
      } catch (err: any) {
        toast.error(err.response?.data?.message || "Failed to load funds");
      } finally {
        setFolioLoading(false);
      }
    };
    fetchInitialUnitholders();
    getAllFunds();
  }, []);

  useEffect(() => {
    if (!unitholderSearchInput) return;
    const timeoutId = setTimeout(() => {
      searchUnitholders(unitholderSearchInput);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [unitholderSearchInput]);

  const CapTxnType = (type: any) => {
    const found = txnTypes.find((t: any) => t.api_name === type);
    return found ? found.dis_name : type;
  };

  // A forPeriodTxns entry is either a dividend/refund-of-capital breakdown
  // row (carries `label`) or a regular transaction (carries `txnr_type`
  // only). Prefer the explicit label when present.
  const rowLabel = (entry: any) =>
    entry?.label ? entry.label : CapTxnType(entry?.txnr_type);

  // Walks a fund's forPeriodTxns in order and computes a running
  // closingUnits balance, since the backend no longer pre-computes this
  // (statementEntries has been removed in favor of returning raw,
  // chronologically-merged transactions).
  //
  // NOTE: folioNoForCalc is now passed in explicitly (rather than closing
  // over the `folioNo` search-box state) because in fund-only mode we
  // compute this once per unitholder, each with their own folio number.
  const withRunningBalance = (
    forPeriodTxns: any[],
    openingUnits: number,
    folioNoForCalc: string,
  ) => {
    let runningUnits = openingUnits || 0;

    return (forPeriodTxns || []).map((entry: any) => {
      if (entry?.label) {
        if (UNIT_ADDING_DIVIDEND_LABELS.includes(entry.label)) {
          runningUnits += entry?.units || 0;
        }
        // Other breakdown labels (Gross Dividend, Taxable Dividend, Tax on
        // Dividend, Dividend Payment) are amount-only and don't move units.
      } else if (entry?.txnr_type === "transferofunits") {
        const parsedTransfree = entry?.transfrees
          ? JSON.parse(entry.transfrees)
          : [];
        const isReceiver =
          parsedTransfree?.filter(
            (fol: any) => fol?.folio_no === folioNoForCalc,
          )?.length > 0;
        if (isReceiver) {
          runningUnits += entry?.units || 0;
        } else {
          runningUnits -= entry?.units || 0;
        }
      } else if (unitSoldArr.includes(entry?.txnr_type)) {
        runningUnits += entry?.units || 0;
      } else if (redeemedArr.includes(entry?.txnr_type)) {
        runningUnits -= entry?.units || 0;
      }

      return { ...entry, closingUnits: runningUnits };
    });
  };

  // Applies withRunningBalance across every fund entry (balance_unit item)
  // for a single unitholder record, whichever mode produced that record.
  const attachRunningBalances = (holderRecord: any) => {
    const folioForCalc = holderRecord?.folio_no || folioNo;
    const balanceUnits = holderRecord?.balance_unit || [];
    return {
      ...holderRecord,
      balance_unit: balanceUnits.map((ele: any) => ({
        ...ele,
        forPeriodTxns: withRunningBalance(
          ele?.forPeriodTxns,
          ele?.openingUnits,
          folioForCalc,
        ),
      })),
    };
  };

  const resetResults = () => {
    setUnitHolderStatement([]);
    setMultiUnitholders([]);
    setIsMultiMode(false);
    setUnitholdersToShow(UNITHOLDERS_PER_PAGE);
    setItemsToShow(10);
  };

  // ---- Single-folio search (existing behaviour, unchanged) ----
  const searchSingleFolioData = async () => {
    const response = await getUnitHolderDividendStatement(
      email,
      fund !== "" && fund !== "all" ? fund : "",
      amc_code,
      folioNo,
      fromDate,
      toDate,
    );

    if (response?.status === 200 && response?.data?.status === 200) {
      setResFolioNo(folioNo);
      setResToDate(toDate);
      setResFromDate(fromDate);

      const balanceUnits = response?.data?.data?.balance_unit || [];
      const withBalances = balanceUnits.map((ele: any) => ({
        ...ele,
        forPeriodTxns: withRunningBalance(
          ele?.forPeriodTxns,
          ele?.openingUnits,
          folioNo,
        ),
      }));

      setUnitHolderStatement(withBalances);
      toast.success("Unitholder Dividend Data Found");
    } else {
      toast.error("No record found!");
    }
  };

  // ---- NEW: fund-only search — every unitholder holding the fund ----
  const searchMultiUnitholderData = async () => {
    const response = await getUnitHolderStatementByFund(
      email,
      fund,
      amc_code,
      "",
      fromDate,
      toDate,
    );

    if (response?.status === 200 && response?.data?.status === 200) {
      setResToDate(toDate);
      setResFromDate(fromDate);

      const records: any[] = response?.data?.data || [];
      const withBalances = records.map((rec: any) =>
        attachRunningBalances(rec),
      );

      setMultiUnitholders(withBalances);
      setIsMultiMode(true);
      setUnitholdersToShow(UNITHOLDERS_PER_PAGE);

      if (withBalances.length === 0) {
        toast.error("No unitholders found holding the selected fund");
      } else {
        toast.success(
          `Found ${withBalances.length} unitholder(s) holding this fund`,
        );
      }

      if (response?.data?.partial_errors?.length) {
        toast.warn(
          `${response.data.partial_errors.length} folio(s) could not be processed`,
        );
      }
    } else {
      toast.error(response?.data?.message || "No record found!");
    }
  };

  const searchData = async () => {
    setItemsPerPage(10);
    resetResults();
    try {
      setLoading(true);

      if (!toDate) {
        setToDateErr("required-border");
        setLoading(false);
        return;
      }
      if (!fromDate) {
        setFromDateErr("required-border");
        setLoading(false);
        return;
      }

      if (!folioNo) {
        // Fund-only mode requires a specific fund to be selected — "all"
        // funds + no folio has no well-defined single result set.
        if (!fund || fund === "all") {
          setFolioNoErr("required-border");
          toast.error(
            "Select a folio number, or choose a specific fund to view all unitholders holding it",
          );
          setLoading(false);
          return;
        }

        await searchMultiUnitholderData();
        setLoading(false);
        return;
      }

      await searchSingleFolioData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Request Failed!");
    } finally {
      setLoading(false);
    }
  };

  const date = new Date();

  const reorderedColumns = DIVIDEND_FIXED_ORDER.filter((col) =>
    selectedValues?.columns?.includes(col),
  );
  const lastCol = reorderedColumns[reorderedColumns.length - 1];

  // ---- Export (Excel / TXT) row building ----
  // holdersOverride lets fund-only mode export every matched unitholder
  // (not just whichever page is currently shown on screen).
  const buildExportRows = (
    holdersOverride?: { folio_no: string; balance_unit: any[] }[],
  ) => {
    const rows: any[] = [];

    if (reorderedColumns.length === 0) {
      return { rows };
    }

    const holders =
      holdersOverride ??
      (isMultiMode
        ? multiUnitholders
        : [{ folio_no: resFolioNo, balance_unit: unitHolderStatement }]);

    holders.forEach((holder) => {
      if (isMultiMode || holdersOverride) {
        const idRow: any = {};
        reorderedColumns.forEach((col, idx) => {
          idRow[col] =
            idx === 0
              ? `Folio: ${holder?.folio_no || ""}${
                  holder?.name ? ` — ${holder.name}` : ""
                }`
              : "";
        });
        rows.push(idRow);
      }

      (holder?.balance_unit || [])
        .slice(0, isMultiMode ? undefined : itemsToShow)
        .forEach((ele: any) => {
          const fundHeading = ele?.fund_name
            ? `${ele?.fund_name} (${ele.fund_code})`
            : ele?.fund_code;

          const headingRow: any = {};
          reorderedColumns.forEach((col, idx) => {
            if (idx === 0) {
              headingRow[col] = fundHeading;
            } else if (col === lastCol) {
              headingRow[col] = selectedValues?.balance?.includes(
                "Opening Balance",
              )
                ? `Opening Units: ${numberWithCommas(
                    parseFloat(ele.openingUnits || 0).toFixed(4),
                  )}`
                : "";
            } else {
              headingRow[col] = "";
            }
          });
          rows.push(headingRow);

          if (ele?.forPeriodTxns?.length > 0) {
            ele.forPeriodTxns.forEach((entry: any) => {
              const txnRow: any = {
                Date: moment(entry?.settlement_date).format("DD-MMM-YYYY"),
                Transaction: rowLabel(entry),
                "Applicable Price":
                  entry?.nav !== null && entry?.nav !== undefined
                    ? parseFloat(entry.nav || 0).toFixed(4)
                    : "",
                Units:
                  entry?.units !== null && entry?.units !== undefined
                    ? numberWithCommas(parseFloat(entry.units || 0).toFixed(4))
                    : "",
                Amount: numberWithCommas(
                  parseFloat(entry?.gross_amount || 0).toFixed(4),
                ),
                "Balance Units": numberWithCommas(
                  parseFloat(entry?.closingUnits || 0).toFixed(4),
                ),
              };

              const filteredRow: any = {};
              reorderedColumns.forEach((col) => {
                filteredRow[col] = txnRow[col] ?? "";
              });
              rows.push(filteredRow);
            });

            const closingRow: any = {};
            reorderedColumns.forEach((col) => {
              if (col === lastCol) {
                closingRow[col] = selectedValues?.balance?.includes(
                  "Closing Balance",
                )
                  ? `Closing Units: ${numberWithCommas(
                      parseFloat(ele.closingUnits || 0).toFixed(4),
                    )}`
                  : "";
              } else {
                closingRow[col] = "";
              }
            });
            rows.push(closingRow);
            if (ele.closingNav > 0) {
              const closingNav: any = {};
              const closingBalance: any = {};
              reorderedColumns.forEach((col) => {
                if (col === lastCol) {
                  closingNav[col] =
                    `Nav: ${parseFloat(ele?.closingNav).toFixed(4) || 0}`;
                } else {
                  closingNav[col] = "";
                }
              });

              if (ele.closingNav > 0) {
                reorderedColumns.forEach((col) => {
                  if (col === lastCol) {
                    closingBalance[col] =
                      `Closing Balance: ${parseFloat(ele.closingBalance || 0).toFixed(4)}`;
                  } else {
                    closingBalance[col] = "";
                  }
                });
              }
              rows.push(closingNav);
              rows.push(closingBalance);
            }
          } else {
            const emptyRow: any = {};
            reorderedColumns.forEach((col, idx) => {
              emptyRow[col] = idx === 0 ? "No Recent Transactions!" : "";
            });
            rows.push(emptyRow);
          }
        });
    });

    return { rows };
  };

  // ---- PDF generation, now reusable across single/combined/separate ----
  const generatePdfFromElement = (
    element: HTMLElement | null,
    filename: string,
    scale = 2,
  ) => {
    return new Promise<void>((resolve, reject) => {
      if (!element) {
        resolve();
        return;
      }

      const styleId = `pdf-flatten-style-${Date.now()}`;
      const styleTag = document.createElement("style");
      styleTag.id = styleId;
      styleTag.innerHTML = `
      table {
        page-break-inside: auto !important;
        border-collapse: collapse !important;
      }
      thead {
        display: table-header-group !important;
      }
      tfoot {
        display: table-footer-group !important;
      }
      tr {
        page-break-inside: avoid !important;
      }
      td, th {
        page-break-inside: auto !important;
      }
      .page-break-avoid {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .statement-page-break {
        page-break-before: always !important;
        break-before: page !important;
      }
    `;
      document.head.appendChild(styleTag);

      const options = {
        filename,
        margin: [10, 3, 10, 3],
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: {
          scale,
          useCORS: true,
          scrollY: 0,
          windowHeight: (element as HTMLElement).scrollHeight,
          letterRendering: true,
          logging: false,
          removeContainer: true,
        },
        jsPDF: {
          unit: "mm",
          format: paperSizeVal || "A4",
          orientation: layoutVal === "Landscape" ? "landscape" : "portrait",
        },
        pagebreak: {
          mode: ["css", "legacy"],
          avoid: [".page-break-avoid"],
          before: [".statement-page-break"],
        },
      };

      html2pdf()
        .from(element)
        .set(options)
        .toPdf()
        .get("pdf")
        .then((pdf: any) => {
          const totalPages = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(10);
            pdf.setTextColor(128, 128, 128);
            const pageHeight = pdf.internal.pageSize.getHeight();
            pdf.text(
              "This is a computer-generated report from MACS 360. No signature is required.",
              10,
              pageHeight - 5,
              { align: "left" },
            );
          }
          return pdf;
        })
        .then((pdf: any) => pdf.output("blob"))
        .then((blob: Blob) => {
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          document.getElementById(styleId)?.remove();
          resolve();
        })
        .catch((err: any) => {
          document.getElementById(styleId)?.remove();
          reject(err);
        });
    });
  };

  const handleGeneratePdf = () => {
    generatePdfFromElement(
      contentRef.current,
      `Unit-Holder-Dividend-Statement-${moment().format("YYYYMMDD")}.pdf`,
    );
  };

  const buildTxnTypeMap = () =>
    (txnTypes || []).reduce((acc: any, t: any) => {
      if (t?.api_name) {
        acc[t.api_name] = t.dis_name;
      }
      return acc;
    }, {});

  const pdfOptions = (filename: string) => ({
    title: "Statement of Account",
    columns: reorderedColumns,
    balance: selectedValues?.balance || [],
    from_date: moment(resFromDate).format("DD-MMM-YYYY"),
    to_date: moment(resToDate).format("DD-MMM-YYYY"),
    report_date: moment().format("DD-MMM-YYYY"),
    paper_size: paperSizeVal || "A4",
    layout: layoutVal || "Portrait",
    logo_base64: sessionStorage.getItem("amc_logo") || null,
    txn_type_map: buildTxnTypeMap(),
    filename,
  });
  const handleDownloadCombinedPdf = async () => {
    if (!multiUnitholders.length) return;

    setDownloadingCombined(true);

    try {
      const filename = `Unit-Holder-Dividend-Statement-Combined-${fund}-${moment().format(
        "YYYYMMDD",
      )}.pdf`;

      const payload = {
        options: pdfOptions(filename),
        unitholders: multiUnitholders,
      };
      // const PDF_SERVICE_URL = "http://127.0.0.1:8000";
      // const response = await fetch(`${PDF_SERVICE_URL}/generate-combined-pdf`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify(payload),
      // });
      const response = await generateCombinedDividendStatementPdf(payload);

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

      toast.success("Combined PDF downloaded");
    } catch (err: any) {
      toast.error("Failed to generate the combined PDF");
    } finally {
      setDownloadingCombined(false);
    }
  };

  const handleDownloadSeparatePdfs = async () => {
    if (!multiUnitholders.length) return;

    setDownloadingSeparate(true);

    const filename = `Unit-Holder-Dividend-Statements-${moment().format(
      "YYYYMMDD",
    )}`;
    try {
      const zipBlob = await downloadSeparatePdfs({
        options: pdfOptions(filename),

        unitholders: multiUnitholders,
      });

      const blob = new Blob([zipBlob.data], {
        type: "application/zip",
      });
      // const url = window.URL.createObjectURL(zipBlob);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Unit-Holder-Dividend-Statements-${moment().format(
        "YYYYMMDD",
      )}.zip`;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${multiUnitholders.length} PDF statement(s)`);
    } catch (err) {
      toast.error("Failed to download PDF statements");
    } finally {
      setDownloadingSeparate(false);
      setSeparateProgress({
        current: 0,
        total: 0,
      });
    }
  };

  const handleDownloadSampleFile = () => {
    const { rows } = buildExportRows();

    const headings = [
      ["Unitholder Dividend Statement"],
      [""],
      ["From Date", "Report Date", "To Date"],
      [
        `${moment(resFromDate).format("DD-MM-YYYY")}`,
        `${moment(date).format("DD-MM-YYYY")}`,
        `${moment(resToDate).format("DD-MM-YYYY")}`,
      ],
      [""],
      [
        "Name",
        "ID",
        "Account Title",
        "Account No",
        "Tax Status",
        "Reinvest",
        "Zakat Exempt",
      ],
      [
        `${unitHolderData?.name || ""}`,
        `${resFolioNo}`,
        `${unitHolderData?.account_title || ""}`,
        `${unitHolderData?.account_no || ""}`,
        `${unitHolderData?.tax_status || ""}`,
        `${unitHolderData?.reinvest === true ? "True" : "False"}`,
        `${unitHolderData?.zakat_exempt === true ? "True" : "False"}`,
      ],
      [""],
    ];

    console.log(rows);

    generateExcel(
      "Unit Holder Dividend Statement Report",
      "Unit Holder Dividend Statement Report",
      "File",
      "File",
      "DCCL",
      headings,
      reorderedColumns,
      rows,
      [],
      [],
    );
  };

  const handleTXT = () => {
    const { rows } = buildExportRows();

    let formattedContent = `"Unitholder Dividend Statement"`;
    formattedContent += `"Name","ID"`;
    formattedContent += `"${
      isMultiMode ? "Multiple Unitholders" : unitHolderData?.name || ""
    }","${isMultiMode ? fund : resFolioNo}",\n`;
    exportToTxt(
      formattedContent,
      { columns: reorderedColumns },
      rows,
      "Unit Holder Dividend Statement Report",
    );
  };

  const previewWidth =
    dimensionOfPages?.find((item: any) => item?.key === paperSizeVal)
      ?.dimensions[layoutVal === "Portrait" ? 0 : 1] ?? "100%";

  // ---- Shared table-rendering block for a single unitholder's fund(s) ----
  // Used both by single-folio mode and (once per holder) by fund-only mode.
  const renderFundTables = (balanceUnitArr: any[]) => (
    <>
      {(balanceUnitArr || []).map((unitHolder: any, index: number) => (
        <div key={index} style={{ marginTop: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "6px",
            }}
          >
            <div style={{ fontSize: "12px" }}>
              <b>Fund:</b> {unitHolder?.fund_code} &nbsp;&nbsp;
              <b>Fund Name:</b> {unitHolder?.fund_name}
            </div>
            {selectedValues?.balance?.includes("Opening Balance") && (
              <div style={{ fontSize: "12px" }}>
                <b>Opening Units:</b>{" "}
                {numberWithCommas(
                  parseFloat(unitHolder?.openingUnits || 0).toFixed(4),
                )}
              </div>
            )}
          </div>

          <table
            className="report"
            style={{
              width: "100%",
              color: "black",
              borderCollapse: "collapse",
              fontSize: "12px",
            }}
          >
            <thead>
              <tr
                style={{
                  borderTop: "2px solid black",
                  borderBottom: "2px solid black",
                }}
              >
                {reorderedColumns.map((col) => (
                  <th
                    key={col}
                    style={{
                      textAlign:
                        col === "Date" || col === "Transaction"
                          ? "left"
                          : "right",
                      padding: "6px",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {unitHolder?.forPeriodTxns?.length > 0 ? (
                unitHolder.forPeriodTxns.map((entry: any, idx: number) => {
                  const cellValues: any = {
                    Date: moment(entry?.settlement_date).format("DD-MMM-YYYY"),
                    Transaction: rowLabel(entry),
                    "Applicable Price":
                      entry?.nav !== null && entry?.nav !== undefined
                        ? parseFloat(entry.nav || 0).toFixed(4)
                        : "",
                    Units:
                      entry?.units !== null && entry?.units !== undefined
                        ? numberWithCommas(
                            parseFloat(entry.units || 0).toFixed(4),
                          )
                        : "",
                    Amount: numberWithCommas(
                      parseFloat(entry?.gross_amount || 0).toFixed(4),
                    ),
                    "Balance Units": numberWithCommas(
                      parseFloat(entry?.closingUnits || 0).toFixed(4),
                    ),
                  };

                  return (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      {reorderedColumns.map((col) => (
                        <td
                          key={col}
                          style={{
                            padding: "6px",
                            textAlign:
                              col === "Date" || col === "Transaction"
                                ? "left"
                                : "right",
                          }}
                        >
                          {cellValues[col]}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={reorderedColumns.length}
                    style={{
                      textAlign: "center",
                      padding: "10px",
                    }}
                  >
                    No Recent Transactions!
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {selectedValues?.balance?.includes("Closing Balance") && (
            <>
              <div
                style={{
                  textAlign: "right",
                  marginTop: "6px",
                  fontSize: "12px",
                }}
              >
                <b>Closing Units:</b>{" "}
                {numberWithCommas(
                  parseFloat(unitHolder?.closingUnits || 0).toFixed(4),
                )}
              </div>
              {unitHolder?.closingNav > 0 && (
                <div
                  style={{
                    textAlign: "right",
                    marginTop: "6px",
                    fontSize: "12px",
                  }}
                >
                  <b>Nav:</b>{" "}
                  {numberWithCommas(
                    parseFloat(unitHolder?.closingNav || 0).toFixed(4),
                  )}
                </div>
              )}
              {unitHolder?.closingNav > 0 && (
                <div
                  style={{
                    textAlign: "right",
                    marginTop: "6px",
                    fontSize: "12px",
                  }}
                >
                  <b>Closing Balance:</b>{" "}
                  {numberWithCommas(
                    parseFloat(unitHolder?.closingBalance || 0).toFixed(4),
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </>
  );

  // ---- Full statement block for one unitholder: header + card + funds ----
  // Used for every holder in fund-only mode (each gets its own header/card
  // so the combined PDF reads as a stack of individual statements), and
  // reused verbatim for the single-PDF-per-holder export loop.
  const renderUnitholderBlock = (holder: any, idx: number) => (
    <div
      key={holder?.folio_no || idx}
      className={
        idx > 0 ? "statement-page-break page-break-avoid" : "page-break-avoid"
      }
      style={{ marginTop: idx > 0 ? "24px" : 0 }}
    >
      <ReportHeader
        title="Statement of Account"
        type="UH"
        DatePattern={`${moment(resFromDate).format("DD-MMM-YYYY")?.replaceAll("-", " ")} — ${moment(resToDate).format("DD-MMM-YYYY")?.replaceAll("-", " ")}`}
        reportDate={moment(resToDate).format("DD-MMM-YYYY")}
      />
      <DividendCard
        unitholderDetails={holder}
        reportName={"UHR"}
        report={true}
        fromDate={moment(resFromDate).format("DD-MM-YYYY")}
        resToDate={moment(resToDate).format("DD-MM-YYYY")}
        toDate={moment().format("DD-MM-YYYY")}
      />
      <div style={{ clear: "both", marginBottom: "4px" }} />
      {reorderedColumns.length > 0 ? (
        renderFundTables(holder?.balance_unit)
      ) : (
        <div className="text-center my-5">
          <b>No column selected</b>
        </div>
      )}
    </div>
  );

  const hasResults = isMultiMode
    ? multiUnitholders.length > 0
    : unitHolderStatement.length > 0;

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Container fluid>
            <ToastContainer limit={1} />
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Unit Holder Dividend Statement</h4>
              </div>
              <div className="card-body">
                <UnitHolderSearchForm
                  fund={fund}
                  folioNo={folioNo}
                  fromDate={fromDate}
                  toDate={toDate}
                  allFunds={allFunds}
                  unitholderOptions={unitholderOptions}
                  folioLoading={folioLoading}
                  unitholderLoading={unitholderLoading}
                  loading={loading}
                  onFundChange={(value) => {
                    setFund(value);
                    resetResults();
                  }}
                  onFolioChange={(selectedOption) => {
                    setFolioNo(selectedOption ? selectedOption.value : "");
                    setUnitHolderData(selectedOption);
                    resetResults();
                  }}
                  onFromDateChange={(value) => {
                    setFromDateErr("");
                    setFromDate(value);
                    resetResults();
                  }}
                  onToDateChange={(value) => {
                    setToDateErr("");
                    setToDate(value);
                    resetResults();
                  }}
                  onSearch={() => searchData()}
                  onUnitholderSearchInput={(value) =>
                    setUnitholderSearchInput(value)
                  }
                  folioNoErr={folioNoErr}
                  fromDateErr={fromDateErr}
                  toDateErr={toDateErr}
                  customStyles={customStyles}
                  theme={theme}
                  isParticipant={false}
                  dividend_statement={true}
                />
                {!folioNo && (
                  <small className="text-muted d-block mt-2">
                    Leave folio number empty and pick a specific fund to
                    generate statements for every unitholder holding that fund.
                  </small>
                )}
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-body">
                {hasResults && (
                  <Row>
                    <ReportTemplates
                      ColumnArr={DIVIDEND_COLUMN_HEADINGS}
                      selectedValues={selectedValues}
                      setSelectedValues={setSelectedValues}
                      pdfExportComponent={pdfExportComponent}
                      setFontSizeVal={setFontSizeVal}
                      fontSizeVal={fontSizeVal}
                      setPaperSizeVal={setPaperSizeVal}
                      paperSizeVal={paperSizeVal}
                      setLayoutVal={setLayoutVal}
                      layoutVal={layoutVal}
                      setScalePDF={setScalePDF}
                      scalePDF={scalePDF}
                      setMarginPDF={setMarginPDF}
                      marginPDF={marginPDF}
                      contentRef={contentRef}
                      fileName={`Unit-Holder-Dividend-Statement-${moment().format("YYYYMMDD")}.pdf`}
                      setScreenSizePerPdf={setScreenSizePerPdf}
                      screenSizePerPdf={screenSizePerPdf}
                      setDimensionOfPages={setDimensionOfPages}
                      printpdf={handleGeneratePdf}
                      printExcel={handleDownloadSampleFile}
                      printTXT={handleTXT}
                    />
                  </Row>
                )}

                {isMultiMode && multiUnitholders.length > 0 && (
                  <Row className="mb-3">
                    <Col className="d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-primary"
                        disabled={downloadingCombined || downloadingSeparate}
                        onClick={handleDownloadCombinedPdf}
                      >
                        {downloadingCombined
                          ? "Preparing Combined PDF..."
                          : `Download Combined Report (${multiUnitholders.length})`}
                      </button>
                      <button
                        className="btn btn-outline-primary"
                        disabled={downloadingCombined || downloadingSeparate}
                        onClick={handleDownloadSeparatePdfs}
                      >
                        {downloadingSeparate
                          ? `Downloading ${separateProgress.current}/${separateProgress.total}...`
                          : `Download Separate PDFs (${multiUnitholders.length})`}
                      </button>
                    </Col>
                  </Row>
                )}

                <div
                  id="printDiv"
                  className="p-4"
                  style={{
                    background: "white",
                    color: "black",
                    minWidth: "200px",
                    overflowX: "auto",
                    width: "auto",
                  }}
                >
                  <div
                    style={{
                      width: previewWidth,
                      height: "auto",
                      margin: "0 auto",
                      backgroundColor: "white",
                    }}
                    className="border"
                  >
                    <PDFExport
                      paperSize={paperSizeVal || "A4"}
                      margin={{
                        top: "1cm",
                        left: "1.5cm",
                        right: "1.5cm",
                        bottom: "1cm",
                      }}
                      scale={scalePDF}
                      landscape={layoutVal === "Landscape"}
                      repeatHeaders={true}
                      fileName={`Unit-Holder-Dividend-Statement-${moment().format("YYYYMMDD")}.pdf`}
                      ref={pdfExportComponent}
                    >
                      <div
                        style={{ margin: "0.1in", whiteSpace: "nowrap" }}
                        ref={contentRef}
                      >
                        {hasResults ? (
                          isMultiMode ? (
                            <div>
                              {multiUnitholders
                                .slice(0, unitholdersToShow)
                                .map((holder, idx) =>
                                  renderUnitholderBlock(holder, idx),
                                )}
                            </div>
                          ) : (
                            <>
                              <ReportHeader
                                title="Statement of Account"
                                type="UH"
                                DatePattern={`${moment(resFromDate).format("DD-MMM-YYYY")?.replaceAll("-", " ")} — ${moment(resToDate).format("DD-MMM-YYYY")?.replaceAll("-", " ")}`}
                                reportDate={moment().format("YYYY-MM-DD")}
                              />
                              <DividendCard
                                unitholderDetails={unitHolderData}
                                reportName={"UHR"}
                                report={true}
                                fromDate={moment(resFromDate).format(
                                  "DD-MM-YYYY",
                                )}
                                resToDate={moment(resToDate).format(
                                  "DD-MM-YYYY",
                                )}
                                toDate={moment().format("DD-MM-YYYY")}
                              />

                              <div
                                style={{ clear: "both", marginBottom: "4px" }}
                              />

                              {reorderedColumns.length > 0 ? (
                                renderFundTables(
                                  unitHolderStatement.slice(0, itemsToShow),
                                )
                              ) : (
                                <div className="text-center my-5">
                                  <b>No column selected</b>
                                </div>
                              )}
                            </>
                          )
                        ) : (
                          <div className="table-empty">
                            <BsTable className="icon" />
                            <p className="text-center empty-text text-primary">
                              No record found
                            </p>
                          </div>
                        )}
                      </div>
                      {hasResults && (
                        <div className="d-flex justify-content-end">
                          <small className="p-2">
                            This is a computer-generated report from MACS 360.
                            No signature is required.
                          </small>
                        </div>
                      )}
                    </PDFExport>
                  </div>
                </div>

                {isMultiMode
                  ? unitholdersToShow < multiUnitholders?.length &&
                    reorderedColumns.length > 0 && (
                      <div className="d-flex flex-column align-items-center my-2">
                        <small className="text-muted mb-1">
                          Showing{" "}
                          {Math.min(unitholdersToShow, multiUnitholders.length)}{" "}
                          of {multiUnitholders.length} unitholders
                        </small>
                        <button
                          className="btn btn-primary mt-1"
                          onClick={() =>
                            setUnitholdersToShow(
                              (prev) => prev + UNITHOLDERS_PER_PAGE,
                            )
                          }
                        >
                          Show More
                        </button>
                      </div>
                    )
                  : itemsToShow < unitHolderStatement?.length &&
                    reorderedColumns.length > 0 && (
                      <div className="d-flex justify-content-center my-2">
                        <button
                          className="btn btn-primary mt-3"
                          onClick={() =>
                            setItemsToShow((prev) => prev + itemsPerPage)
                          }
                        >
                          Show More
                        </button>
                      </div>
                    )}
              </div>
            </div>
          </Container>
        </Col>
      </Row>

      {/*
        Hidden, off-screen render target used only while
        "Download Separate PDFs" loops through each unitholder one at a
        time. Kept out of the flow (position: fixed, far off left) so
        html2canvas can still capture it without flashing on screen.
      */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: "-99999px",
          background: "white",
          color: "black",
          width: previewWidth,
        }}
        aria-hidden="true"
      >
        <div ref={exportRef} style={{ margin: "0.1in", whiteSpace: "nowrap" }}>
          {exportSingleHolder && renderUnitholderBlock(exportSingleHolder, 0)}
          {exportSingleHolder && (
            <div className="d-flex justify-content-end">
              <small className="p-2">
                This is a computer-generated report from MACS 360. No signature
                is required.
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnitHolderDividendStatement;
